// Tabs block (app/Views/event/partials/tabs_block.php). The server renders every tab as a heading followed by its
// panel, all visible. This script turns each heading into a button and keeps one panel open:
//  - from 768 px the buttons move into the block's `.rem-tabs__buttons` container, which becomes a real tablist
//    (role=tab, aria-selected, roving tabindex, Arrow keys, Home and End; the panels become tabpanels);
//  - below, the buttons sit above their panels as an accordion (aria-expanded, no tab roles).
// Without the script every panel stays visible.

export const WIDE = '(min-width: 768px)';

// The tab an arrow key moves to from `index`, or -1 for any other key.
export const targetIndex = (key, index, count) => {
    switch (key) {
        case 'ArrowRight':
        case 'ArrowDown':
            return (index + 1) % count;
        case 'ArrowLeft':
        case 'ArrowUp':
            return (index - 1 + count) % count;
        case 'Home':
            return 0;
        case 'End':
            return count - 1;
        default:
            return -1;
    }
};

const toButton = (heading) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.id = heading.id;
    button.className = heading.className;
    button.dataset.remPanel = heading.dataset.remPanel;
    button.setAttribute('aria-controls', heading.dataset.remPanel);
    while (heading.firstChild) {
        button.appendChild(heading.firstChild);
    }
    heading.replaceWith(button);

    return button;
};

// Selects `index` in the current mode and sets the attributes that mode needs.
export const select = (state, index) => {
    const { tabs, panels, wide } = state;
    state.index = index;
    tabs.forEach((tab, i) => {
        const open = i === index;
        if (wide) {
            tab.setAttribute('aria-selected', open ? 'true' : 'false');
            tab.tabIndex = open ? 0 : -1;
            panels[i].setAttribute('role', 'tabpanel');
            panels[i].setAttribute('aria-labelledby', tab.id);
            panels[i].tabIndex = 0;
        } else {
            tab.setAttribute('aria-expanded', open ? 'true' : 'false');
            tab.tabIndex = 0;
        }
        panels[i].hidden = !open;
    });
};

const setMode = (state, wide) => {
    const { block, host, tabs, panels, items } = state;
    state.wide = wide;
    tabs.forEach((tab, i) => {
        ['aria-selected', 'aria-expanded'].forEach((name) => tab.removeAttribute(name));
        ['role', 'aria-labelledby'].forEach((name) => panels[i].removeAttribute(name));
        panels[i].removeAttribute('tabindex');
        if (wide) {
            tab.setAttribute('role', 'tab');
            host.appendChild(tab);
        } else {
            tab.removeAttribute('role');
            items[i].insertBefore(tab, panels[i]);
        }
    });
    if (wide) {
        host.setAttribute('role', 'tablist');
        host.setAttribute('aria-orientation', block.dataset.remTabsOrientation === 'vertical' ? 'vertical' : 'horizontal');
    } else {
        host.removeAttribute('role');
        host.removeAttribute('aria-orientation');
    }
    select(state, state.index);
};

export const initTabs = (root) => {
    root.querySelectorAll('[data-rem-tabs]').forEach((block) => {
        if (block.dataset.remTabsBound === '1') {
            return;
        }
        block.dataset.remTabsBound = '1';
        const items = Array.from(block.querySelectorAll('.rem-tabs__item'));
        const host = block.querySelector('.rem-tabs__buttons');
        if (items.length === 0 || !host) {
            return;
        }
        const tabs = items.map((item) => toButton(item.querySelector('.rem-tabs__tab')));
        const panels = tabs.map((tab) => document.getElementById(tab.dataset.remPanel));
        if (panels.some((panel) => !panel)) {
            return;
        }
        const state = { block, host, items, tabs, panels, index: 0, wide: false };
        tabs.forEach((tab, i) => {
            tab.addEventListener('click', () => select(state, i));
            tab.addEventListener('keydown', (event) => {
                const next = state.wide ? targetIndex(event.key, i, tabs.length) : -1;
                if (next >= 0) {
                    event.preventDefault();
                    select(state, next);
                    tabs[next].focus();
                }
            });
        });
        const query = window.matchMedia(WIDE);
        setMode(state, query.matches);
        query.addEventListener('change', (event) => setMode(state, event.matches));
        block.classList.add('is-ready');
    });
};
